import { useState, useMemo, useEffect, useContext } from "react"
import {
  useOrganizacionTags,
  useCreateOrganizacionTag,
  useDeleteOrganizacionTag,
} from "hooks/useOrganizacionTags"
import AuthContext from "context/auth/authContext"
import ProjectContext from "context/projects/projectContext"
import i18n from "i18n"
import {
  createStateTag as createStateTagUtil,
  createAndAssignDefaultTag as createAndAssignDefaultTagUtil,
  deleteTag as deleteTagUtil,
  handleKeyDown as handleKeyDownUtil,
} from "./stateTagUtils"

const options = [
  {
    state: "E10",
    label: i18n.t("tags.start"),
    color: "#eeeeee",
    tags: [{ name: i18n.t("tags.pending"), color: "#eeeeee" }],
  },
  {
    state: "E20",
    label: i18n.t("tags.inProgress"),
    color: "#ffecb3",
    tags: [{ name: i18n.t("tags.inProgress"), color: "#ffecb3" }],
  },
  {
    state: "E30",
    label: i18n.t("tags.complete"),
    color: "#dcedc8",
    tags: [
      { name: i18n.t("tags.finished"), color: "#dcedc8" },
      { name: i18n.t("tags.rejected"), color: "#ffd4ca" },
    ],
  },
]

export const useStateTagLogic = projectRow => {
  const [hiddenTags, setHiddenTags] = useState([])
  const [anchorEl, setAnchorEl] = useState(null)
  const [showInputIdx, setShowInputIdx] = useState(null)
  const [inputValue, setInputValue] = useState("")
  const [localTags, setLocalTags] = useState([])

  const authContext = useContext(AuthContext)
  const projectContext = useContext(ProjectContext)
  const { user } = authContext
  const { updateStateTag } = projectContext

  const createTagMutation = useCreateOrganizacionTag()
  const deleteTagMutation = useDeleteOrganizacionTag()

  const organizationId = user?.["custom:organizationId"]
  const enabled = !!organizationId
  const { data: tags, isLoading } = useOrganizacionTags(organizationId, enabled)

  const open = Boolean(anchorEl)

  const tagsByState = useMemo(() => {
    const allTags = [...(tags || []), ...localTags]
    const visibleTags = allTags.filter(tag => !hiddenTags.includes(tag.SK))

    // Usar Map para evitar duplicados basándose en nombre + estado
    const uniqueTagsMap = new Map()
    const duplicateIds = []

    visibleTags.forEach(tag => {
      const key = `${tag.properties.name.toLowerCase()}_${tag.properties.state}`

      if (uniqueTagsMap.has(key)) {
        duplicateIds.push(tag.SK)
      } else {
        uniqueTagsMap.set(key, tag)
      }
    })

    if (duplicateIds.length > 0) {
      setHiddenTags(prev => [...new Set([...prev, ...duplicateIds])])
    }

    const uniqueTags = Array.from(uniqueTagsMap.values())
    return uniqueTags.reduce((acc, tag) => {
      const state = tag.properties.state
      if (!acc[state]) acc[state] = []
      acc[state].push(tag)
      return acc
    }, {})
  }, [tags, localTags, hiddenTags])

  const currentStateTag = useMemo(() => {
    if (!projectRow?.stateTag || isLoading) return null

    const allTags = [...(tags || []), ...localTags]
    const currentTag = allTags.find(tag => tag.SK === projectRow.stateTag)

    if (currentTag) {
      return {
        name: currentTag.properties.name,
        color: currentTag.properties.color,
      }
    }

    // Buscar en options si el stateTag coincide con el estado y alguno de los tags por defecto
    const defaultOption = options.find(
      option => option.state === projectRow.stateTag,
    )
    if (
      defaultOption &&
      Array.isArray(defaultOption.tags) &&
      defaultOption.tags.length > 0
    ) {
      // Buscar el tag por defecto que coincida con el stateTag
      const foundTag = defaultOption.tags.find(
        t => t.name === projectRow.stateTag,
      )
      if (foundTag) {
        return {
          name: foundTag.name,
          color: foundTag.color,
        }
      }
      // Si no, mostrar el primero
      return {
        name: defaultOption.tags[0].name,
        color: defaultOption.tags[0].color,
      }
    }
    return null
  }, [projectRow?.stateTag, tags, localTags, isLoading])

  useEffect(() => {
    if (tags) {
      setLocalTags(localTags =>
        localTags.filter(
          localTag =>
            !tags.some(
              tag =>
                tag.properties.name === localTag.properties.name &&
                tag.properties.state === localTag.properties.state,
            ),
        ),
      )
    }
  }, [tags])

  useEffect(() => {
    if (tags && tags.length > 0) {
      const duplicateMap = new Map()
      const toDelete = []

      tags.forEach(tag => {
        const key = `${tag.properties.name.toLowerCase()}_${tag.properties.state}`

        if (duplicateMap.has(key)) {
          toDelete.push(tag.SK)
        } else {
          duplicateMap.set(key, tag.SK)
        }
      })

      toDelete.forEach(tagSK => {
        deleteTagMutation.mutate({
          organization_id: organizationId,
          sk: tagSK,
        })
      })
    }
  }, [tags, deleteTagMutation, organizationId])

  const handleClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setShowInputIdx(null)
  }

  const createStateTag = async tag => {
    await createStateTagUtil(tag, projectRow, updateStateTag, handleClose)
  }

  const createAndAssignDefaultTag = async option => {
    let customOption = { ...option }
    if (
      (option.tag &&
        option.tag.toLowerCase() === i18n.t("tags.rejected").toLowerCase()) ||
      (option.name &&
        option.name.toLowerCase() === i18n.t("tags.rejected").toLowerCase())
    ) {
      customOption = { ...option, color: "#ffd4ca" }
    }
    await createAndAssignDefaultTagUtil(
      customOption,
      organizationId,
      createTagMutation,
      setLocalTags,
      createStateTag,
    )
  }

  const deleteTag = async tag => {
    await deleteTagUtil(tag, organizationId, deleteTagMutation, setHiddenTags)
  }

  const handleKeyDown = (e, option) => {
    handleKeyDownUtil(
      e,
      option,
      inputValue,
      tags,
      localTags,
      organizationId,
      createTagMutation,
      setLocalTags,
      setInputValue,
      setShowInputIdx,
    )
  }

  return {
    anchorEl,
    open,
    showInputIdx,
    inputValue,
    setInputValue,
    setShowInputIdx,

    tagsByState,
    currentStateTag,
    isLoading,
    options,

    handleClick,
    handleClose,
    createStateTag,
    createAndAssignDefaultTag,
    deleteTag,
    handleKeyDown,
  }
}
